import React, { useMemo } from "react";
import { Avatar, Box, Typography, Button, LinearProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import FaceOutlinedIcon from "@mui/icons-material/FaceOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";

import TokenIcon from "@mui/icons-material/Token";

import { authService } from "../../../services/authService";
import { useTokenUsage } from '../../../hooks/useTokenUsage';


const generalSettings = [
  {
    type: "theme",
    icon: (
      <ColorLensOutlinedIcon
        sx={{ fontSize: 32, color: "primary.main", mr: 2 }}
      />
    ),
    button_path: "/settings/theme",
    title: "ตั้งค่าธีม",
    description:
      "ผู้ใช้งานสามารถปรับแต่งธีมหลักของระบบได้ รวมถึงการตั้งค่าสี Accent และโหมดสีพื้นหลัง (เช่น โหมดสว่าง โหมดมืด หรือ โหมดอัตโนมัติตามระบบของผู้ใช้งาน) เพื่อให้เหมาะกับการใช้งานมากยิ่งขึ้น",
  },
  {
    type: "assistance",
    icon: (
      <FaceOutlinedIcon sx={{ fontSize: 32, color: "primary.main", mr: 2 }} />
    ),
    button_path: "/settings/assistance",
    title: "ตั้งค่าบทบาทผู้ช่วย",
    description:
      "คุณสามารถเลือกอวาตาร์ส่วนตัวที่คุณสนใจเพื่อแสดงความเป็นเอกลักษณ์บนแพลตฟอร์มได้ อวาตาร์นี้จะเป็นผู้ช่วยในการตอบคำถามของคุณ และแสดงตัวตนของคุณให้โดดเด่น และน่าสนใจยิ่งขึ้น",
  },
];

const adminSettings = [
  {
    type: "manage_users",
    icon: (
      <AdminPanelSettingsOutlinedIcon
        color="warning"
        sx={{ fontSize: 32, mr: 2 }}
      />
    ),
    button_path: "/settings/users",
    title: "ข้อมูลผู้ใช้งาน",
    description:
      "สำหรับผู้ดูแลระบบ คุณสามารถตรวจสอบข้อมูลผู้ใช้งานในระบบ ตรวจสอบประวัติการใช้งานโทเค็นต่างๆ เพื่อให้เหมาะสมกับการใช้งานในองค์กรของคุณ",
  },
  {
    type: "manage_documents",
    icon: <TopicOutlinedIcon color="warning" sx={{ fontSize: 32, mr: 2 }} />,
    button_path: "/settings/documents",
    title: "ข้อมูลเอกสาร",
    description:
      "สำหรับผู้ดูแลระบบ คุณสามารถตรวจสอบข้อมูลเอกสารในระบบ รวมไปถึงจัดการเพิ่ม ลบ หรือแก้ไขเอกสาร เพื่อให้เหมาะสมกับการใช้งานในองค์กรของคุณ",
  },
];

interface SettingsHomeProps {
  handleNavigate: (path: string) => void;
}

const SettingsHome: React.FC<SettingsHomeProps> = ({ handleNavigate }) => {
  const theme = useTheme();

  const authData = authService.getAuthData();
  const userInfo = authData?.user_info;
  const { tokenUsage, isLoading, formatTokenCount } = useTokenUsage(userInfo?.email);

  // Sample profile data
  const name = userInfo?.name || "?";
  const email = userInfo?.email || "ไม่มีข้อมูลแสดงผล";

  // Generate avatar initials from user's name
  const avatarInitials = useMemo(() => {
    if (!userInfo?.name) return "?";
    return userInfo.name
      .split(" ")
      .map((part: string) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [userInfo?.name]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Avatar and name */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 4,
          pb: 8,
          width: "100%",
          maxWidth: 800,
          margin: "auto",
          position: "relative",
        }}
      >
        <Avatar
          alt={userInfo?.name || "User"}
          variant="rounded"
          sx={{
            fontSize: 36,
            width: 72,
            height: 72,
            mb: 4,
            bgcolor: "primary.main",
            position: "relative",
            zIndex: 1,
          }}
        >
          {avatarInitials}
        </Avatar>
        <Typography
          variant="h5"
          color="textPrimary"
          sx={{
            position: "relative",
            textAlign: "center",
            mb: 1,
            zIndex: 1,
            fontWeight: 600,
          }}
        >
          ยินดีต้อนรับคุณ {name}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ position: "relative", textAlign: "center", mb: 1, zIndex: 1 }}
        >
          {email.toLowerCase()}
        </Typography>
      </Box>

      {/* Token Usage */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          padding: 2,
          borderRadius: 2,
          width: "100%",
          maxWidth: 800,
          border: "1px solid",
          borderColor: "divider",
          mb: 4,
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <TokenIcon sx={{ fontSize: 32, color: "primary.main", mr: 2 }} />
          <Box>
            <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600, mb: 0.5 }}>
              การใช้งานโทเค็น
            </Typography>
            {isLoading ? (
              <Typography variant="body2" color="textSecondary">
                กำลังโหลดข้อมูล...
              </Typography>
            ) : tokenUsage ? (
              <>
                <Typography variant="body2" color="textSecondary">
                  จำนวนโทเค็นที่ใช้: {formatTokenCount(tokenUsage.total_tokens.value)} โทเค็น
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ค่าใช้จ่าย: {tokenUsage.total_cost.value.toFixed(2)} ดอลลาร์
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                ไม่พบข้อมูลการใช้งาน
              </Typography>
            )}
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={tokenUsage ? Math.min((tokenUsage.total_tokens.value / 1000000) * 100, 100) : 0}
          sx={{
            width: `calc(100% - 48px)`,
            flexGrow: 1,
            ml: 6,
            mt: 2,
            borderRadius: 4,
          }}
        />
      </Box>

      {/* General Settings */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: 800,
          mb: 2,
        }}
      >
        <Typography
          variant="body1"
          color="textPrimary"
          sx={{ fontWeight: 600, mt: 2, mb: 2 }}
        >
          ตั้งค่าทั่วไป
        </Typography>
        {generalSettings.map((setting) => (
          <Box
            key={setting.type}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              padding: 2,
              borderRadius: 2,
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
              }}
            >
              {setting.icon}
              <Box flexGrow={1}>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {setting.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {setting.description}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="text"
              sx={{
                ml: 5,
                mt: 2,
                textAlign: "center",
              }}
              onClick={() => handleNavigate(setting.button_path)}
            >
              {setting.title}
            </Button>
          </Box>
        ))}
      </Box>

      {/* Admin Settings */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: 800,
        }}
      >
        <Typography
          variant="body1"
          color="textPrimary"
          sx={{ fontWeight: 600, mt: 2, mb: 2 }}
        >
          สำหรับผู้ดูแลระบบ
        </Typography>
        {adminSettings.map((setting) => (
          <Box
            key={setting.type}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              padding: 2,
              borderRadius: 2,
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
              }}
            >
              {setting.icon}
              <Box flexGrow={1}>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {setting.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {setting.description}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="text"
              color="warning"
              sx={{
                ml: 5,
                mt: 2,
                textAlign: "center",
              }}
              onClick={() => handleNavigate(setting.button_path)}
            >
              {setting.title}
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SettingsHome;
